import React, { useState, useRef, useEffect } from 'react';
import { useStaticQuery, graphql, Link } from 'gatsby';
import styled from 'styled-components';
import FadeIn from 'react-fade-in';
import _ from 'lodash';

import Text from '@basic/Text';
import HTMLWrapper from '@basic/HTMLWrapper';
import MainNavCTA from './elements/MainNavCTA';
import MainLogo from './elements/MainLogo';
import Arrow from './elements/NavigationArrow';
import HamburgerIcon from './elements/HamburgerIcon';

import { HiArrowLongRight } from 'react-icons/hi2';

import MainNavigationMobile from './MainNavigationMobile';
import {
	IconLink,
	ChevronLink,
	FullFeatureLink,
	SubtextLink,
	LargeFeatureLink,
} from './elements/MenuLinks';

// TODO

// Update scrolling background color to be more responsive to light mode or dark mode.
// Update fonts and link sizes to match the new design system.
// Stop Navigation at the footer

// --- SECTIONS

const NavSectionWrapper = styled.div`
	width: 90%;
	height: 100%;
	max-width: 1320px;
	margin: 0 auto;
	display: ${({ display }) => display};
	padding: ${({ padding }) => padding};
`;

const NavSection = ({
	children,
	display,
	padding,
	className,
	onMouseLeave,
}) => {
	return (
		<NavSectionWrapper
			onMouseLeave={onMouseLeave}
			display={display}
			padding={padding}
			className={className}
		>
			{children}
		</NavSectionWrapper>
	);
};

// ------- MENU ELEMENTS

const ColumnHeaderWrapper = styled.span`
	.column-header__title {
		font-family: ${({ theme }) => theme.fonts.callout600};
		font-size: 1.3rem; /* 13px */
		line-height: 100%; /* 13px */
		letter-spacing: 0.13px;
		text-transform: uppercase;

		&.large {
			font-family: ${({ theme }) => theme.fonts.subheadingMedium};
			font-size: 1.6rem;
			line-height: 100%; /* 16px */
			letter-spacing: 0.16px;
			text-transform: none;
			letter-spacing: 0;
			margin-bottom: 0;
		}

		svg {
			fill: ${({ theme }) => theme.colors.black.main};
			width: 30px;
			height: 30px;
			margin-left: 8px;
			transform-origin: left center;
			transform: scaleX(1);
			transition: transform 0.5s ease-in-out;
		}

		:hover {
			&.large {
				color: ${({ theme }) => theme.colors.primary.main};
			}
			svg {
				fill: ${({ theme }) => theme.colors.primary.main};
				transform: scaleX(1.3); /* Doubles the width */
			}
		}
	}
	.column-header__sub-title {
		font-size: 1.5rem;
		font-family: ${({ theme }) => theme.fonts.body};
		line-height: 24px; /* 160% */
		letter-spacing: 0.15px;
		color: ${({ theme }) => theme.colors.black.main};
	}
`;

const ColumnHeader = ({ menuTitleLink, menuTitle, menuTitleSubhead }) => {
	return (
		<ColumnHeaderWrapper>
			{menuTitleLink ? (
				<Link to={menuTitleLink}>
					<Text component="b5" className="column-header__title large">
						{menuTitle} <HiArrowLongRight />
					</Text>
					<HTMLWrapper
						html={menuTitleSubhead.childMarkdownRemark.html}
						className="column-header__sub-title"
					/>
				</Link>
			) : (
				<Text
					component="b5"
					className="column-header__title"
					css={{ marginBottom: 16 }}
				>
					{menuTitle}
				</Text>
			)}
		</ColumnHeaderWrapper>
	);
};

// ------- SUBMENUS

const ProductInnerWrapper = styled(NavSection)`
	position: relative;
	gap: 56px;
	grid-template-columns: auto 1fr;
	max-width: 1200px;

	div {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}
`;

const SolutionsInnerWrapper = styled(NavSection)`
	position: relative;
	grid-template-columns: repeat(4, auto);
	max-width: 1200px;

	div {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
`;

const ResourcesInnerWrapper = styled(NavSection)`
	position: relative;
	grid-template-columns: auto 1fr;
	max-width: 1200px;

	div {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
`;

const SubMenu = ({ subMenu, children, ref }) => {
	return (
		<>
			{/* Product: Two-column menu with accent column */}
			{subMenu === 'Products' && (
				<ProductInnerWrapper display="grid"> {children}</ProductInnerWrapper>
			)}
			{/* Solutions: Four-column menu with accent column */}
			{subMenu === 'Solutions' && (
				<SolutionsInnerWrapper display="grid">{children}</SolutionsInnerWrapper>
			)}
			{/* Resources: Two-column menu with accent column */}
			{subMenu === 'Resources' && (
				<ResourcesInnerWrapper display="grid">{children}</ResourcesInnerWrapper>
			)}
		</>
	);
};

// ------- SUBMENU COLUMNS

const SubmenuColumnWrapper = styled(FadeIn)`
	width: 100%;
	position: relative;
	padding-top: ${({ accentColumn }) => (accentColumn ? '33px' : '0px')};
	padding-bottom: ${({ accentColumn }) => (accentColumn ? '38px' : '0px')};
	margin: ${({ accentColumn }) =>
		accentColumn ? '0px 0px 0px' : '33px 0 38px'};
	border-left: ${({ borderColumn, firstColumn, accentColumn }) =>
		borderColumn && !firstColumn && !accentColumn
			? `1px solid rgba(108, 119, 136, 0.50)`
			: 'none'};
	padding-left: ${({ borderColumn, accentColumn, firstColumn }) =>
		accentColumn ? '55px' : borderColumn && !firstColumn ? '30px' : '0px'};
	padding-right: 30px;
	background-color: ${({ accentColumn }) => accentColumn && '#f0f0f0;'};

	${({ accentColumn }) =>
		accentColumn &&
		`:before {
            content: '';
            position: absolute;
            top: 0;
            left: 100%;
            width: 100vw;
            height: 100%;
            background-color: inherit;
	}`};
`;

const SubmenuColumn = ({ column, subMenu, lastColumn, firstColumn }) => {
	const accentColumn =
		subMenu === 'Products' ||
		subMenu === 'Solutions' ||
		subMenu === 'Resources';
	const borderColumn = subMenu === 'Solutions';

	return (
		<SubmenuColumnWrapper
			borderColumn={borderColumn}
			accentColumn={accentColumn && lastColumn}
			firstColumn={firstColumn}
		>
			<ColumnHeader
				menuTitleLink={column.menuTitleLink}
				menuTitle={column.menuTitle}
				menuTitleSubhead={column.menuTitleSubhead}
			/>
			{column?.menuItems?.map(menuItem => {
				if (menuItem?.linkImage && !menuItem.featureItem) {
					return (
						<IconLink
							link={menuItem.linkUrl}
							image={menuItem?.linkImage.file.url}
							header={menuItem.linkMainText}
						/>
					);
				} else if (!menuItem?.linkImage && menuItem.linkSubTextLong) {
					return (
						<SubtextLink
							link={menuItem.linkUrl}
							header={menuItem.linkMainText}
							subheader={menuItem.linkSubTextLong.linkSubTextLong}
						/>
					);
				} else if (menuItem.featureItem && !menuItem.linkMainText) {
					return (
						<FullFeatureLink
							link={menuItem.linkUrl}
							image={menuItem?.linkImage.file.url}
							subheader={menuItem.linkSubTextLong.linkSubTextLong}
						/>
					);
				} else if (menuItem.featureItem && menuItem.linkMainText) {
					return (
						<LargeFeatureLink
							link={menuItem.linkUrl}
							header={menuItem.linkMainText}
							image={menuItem?.linkImage.file.url}
							subheader={menuItem.linkSubTextLong.linkSubTextLong}
						/>
					);
				} else {
					return (
						<ChevronLink
							link={menuItem.linkUrl}
							header={menuItem.linkMainText}
						/>
					);
				}
			})}
			{column.menuColumnLinkText && (
				<ChevronLink
					link={column.menuColumnUrl}
					header={column.menuColumnLinkText}
				/>
			)}
		</SubmenuColumnWrapper>
	);
};

const Container = styled.div`
	min-width: 100vw;
	background-color: ${props =>
		props.isScrolled ? 'rgba(255,255, 255, 0.5)' : 'transparent'};
	backdrop-filter: ${props => (props.isScrolled ? 'blur(12px)' : 'none')};
	height: ${({ theme }) => theme.fixedHeights.navBar}px;
`;

const TopNavBarInner = styled(NavSection)`
	display: flex;
	justify-content: center;
	align-items: center;
	position: relative;
`;

const MainMenu = styled.div`
	display: flex;
	align-items: center;
	height: 100%;
	width: 100%;
	justify-content: space-between;
`;

const MainMenuItems = styled.div`
	display: none;

	${({ theme }) => theme.mediaQuery.breakpointLg} {
		display: flex;
		align-items: center;
		gap: 24px;
	}
`;

const TopLevelLinkText = styled.span`
	font-family: ${({ theme }) => theme.fonts.subheadingMedium};
	font-size: 1.5rem;
	line-height: 100%; /* 15px */
	letter-spacing: 0.15px;
	position: relative;
	color: ${props =>
		props.darkMode
			? props.theme.colors.white.main
			: props.theme.colors.black.main};

	&:hover {
		cursor: pointer;
	}
`;

const CalloutLink = styled(Link)`
	display: flex;
	background-color: ${({ theme }) => theme.colors.white.main};
	padding: 7px 12px;
	border-radius: 5px;
	color: ${({ theme }) => theme.colors.black.main};

	:hover {
		background-color: ${({ theme }) => theme.colors.black.main};
		color: ${({ theme }) => theme.colors.white.main};

		img {
			filter: invert(1);
		}
	}

	img {
		margin-right: 5px;
		max-width: 20px;
	}
`;

const StandardNavLink = styled(Link)`
	display: flex;
	font-family: ${({ theme }) => theme.fonts.subheadingMedium};
	color: ${({ darkMode, theme }) =>
		darkMode ? theme.colors.white.main : theme.colors.black.main};

	&:hover {
		color: ${({ darkMode, theme }) =>
			darkMode ? theme.colors.white.main : theme.colors.black.main};


		&:after {
			content: '';
			width: 100%;
			height: 2px;
			background ${({ theme }) => theme.colors.primary.main};
			position: absolute;
			bottom: -10px;
		}
	}
`;

const ExpandingNavLink = styled.span`
	transition-duration: 0.1s;
	display: flex;
	align-items: center;
	gap: 0.5rem;

	:hover {
		&:after {
			content: '';
			width: 100%;
			height: 2px;
			background ${({ theme }) => theme.colors.primary.main};
			position: absolute;
			bottom: -10px;
		}
	}
`;

const SubMenuWrapper = styled.div`
	display: none;

	${({ theme }) => theme.mediaQuery.breakpointLg} {
		display: block;
		min-width: 100vw;
		background: ${({ theme }) => theme.colors.white.main};
	}
`;

const DesktopMainNavCTA = styled(MainNavCTA)`
	display: none;

	${({ theme }) => theme.mediaQuery.breakpointLg} {
		display: block;
	}
`;

export default React.memo(({ darkMode, className }) => {
	const menu = useStaticQuery(graphql`
		query MainMenuQuery {
			contentfulMenu(contentful_id: { eq: "2iPT0UXwQDnf66xfYXpU9m" }) {
				...MainNavQuery
			}
		}
	`);

	const [currentIndex, setCurrentIndex] = useState(null);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [mobileInView, setMobileInView] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	// scrolling will set a white background
	const reverseColor = darkMode && !isScrolled;

	const topLevel = menu?.contentfulMenu.menuItems;

	const navRef = useRef(null);
	const subMenuRef = useRef(null);

	const resetMenu = e => {
		if (
			e.relatedTarget instanceof HTMLElement &&
			(navRef?.current.contains(e.relatedTarget) ||
				subMenuRef?.current.contains(e.relatedTarget))
		) {
			return;
		} else {
			setCurrentIndex(null);
		}
	};

	// takes .75s for mobile menu to close...
	useEffect(() => {
		if (mobileOpen) {
			setMobileInView(true);
		} else if (!mobileOpen) {
			setTimeout(() => {
				setMobileInView(false);
			}, 500);
		}
	}, [mobileOpen]);

	useEffect(() => {
		const handleScroll = () => {
			const scrollTop = window.scrollY;
			setIsScrolled(scrollTop > 50);
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	return (
		<Container
			key="main-menu-container"
			mobileOpen={mobileInView}
			className={`${className} main-menu-container`}
			isScrolled={isScrolled}
		>
			<TopNavBarInner
				onMouseLeave={resetMenu}
				className="main-menu-container-inner"
			>
				<MainMenu ref={navRef}>
					<MainLogo reverse={reverseColor} />

					<MainMenuItems>
						{topLevel?.map((menu, index) => {
							return (
								<TopLevelLinkText
									className="navigation__top-link"
									onMouseEnter={() => setCurrentIndex(index)}
									onFocus={() => setCurrentIndex(index)}
									active={currentIndex === index}
									component="b5"
									semiBold
									tabIndex="0"
									index={index}
									darkMode={reverseColor}
									key={`item-${menu.contentful_id}`}
								>
									{menu.contentful_id === 'WuBkoE3T2gquta2BfyuZT' ? (
										<CalloutLink
											to={menu.linkUrl}
											darkMode={reverseColor}
											className="menu__callout-link"
										>
											<img src={menu.linkImage.file.url} alt="" />
											{menu.linkMainText}
										</CalloutLink>
									) : menu.linkUrl ? (
										<StandardNavLink to={menu.linkUrl} darkMode={reverseColor}>
											{menu.linkMainText}
										</StandardNavLink>
									) : (
										<ExpandingNavLink>
											{menu.menuTitle}
											<Arrow
												className="arrow"
												reverse={reverseColor}
												rotating={currentIndex === index}
											/>
										</ExpandingNavLink>
									)}
								</TopLevelLinkText>
							);
						})}
					</MainMenuItems>
					<DesktopMainNavCTA darkMode={reverseColor} />
					<HamburgerIcon
						open={mobileOpen}
						toggle={() => setMobileOpen(!mobileOpen)}
					/>
				</MainMenu>
			</TopNavBarInner>

			<SubMenuWrapper
				onMouseLeave={resetMenu}
				key="sub-menu-container"
				ref={subMenuRef}
			>
				<SubMenu subMenu={topLevel[currentIndex]?.menuTitle}>
					{topLevel[currentIndex]?.menuItems?.map((column, index) => {
						return (
							<SubmenuColumn
								column={column}
								index={index}
								currentIndex={currentIndex}
								subMenu={topLevel[currentIndex]?.menuTitle}
								lastColumn={
									index === topLevel[currentIndex]?.menuItems.length - 1
								}
								firstColumn={index === 0}
							/>
						);
					})}
				</SubMenu>
			</SubMenuWrapper>
			<MainNavigationMobile
				key="mobile-menu"
				open={mobileOpen}
				menu={menu?.contentfulMenu.menuItems}
				toggle={() => setMobileOpen(!mobileOpen)}
			/>
		</Container>
	);
});
